// ── 녹음 업로드 자동 재시도 (PORTAL-12581) ──────────────────────────
// 긴 회의·느린 회선에서 일시적으로 끊긴 업로드가 곧바로 백업 큐로 빠지지 않게,
// 일시적 실패만 골라 간격을 늘려 가며 다시 올린다. 전송 상한은 업로드 watchdog
// (UploadStallError, PORTAL-12593)이 맡으므로 여기선 고정 timeout 을 두지 않는다.
export const UPLOAD_RETRY_DELAYS_MS = [2000, 5000];

type AxiosLikeError = {
  code?: string;
  response?: { status?: number };
  name?: string;
};

/**
 * 다시 올리면 성공할 수 있는 실패인가.
 *  - 업로드 멈춤(스톨) · 응답 없는 네트워크 오류 · 시간 초과
 *  - 서버 일시 오류(5xx) · 408 · 429
 * 4xx(권한·형식 등)와 사용자가 취소한 요청은 다시 올려도 같은 결과라 재시도하지 않는다.
 */
export const isRetryableUploadError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const e = error as AxiosLikeError;
  // UploadStallError(hooks/apiHooks) — 이름으로 가려 무거운 훅 모듈을 끌어오지 않는다
  if (e.name === 'UploadStallError') return true;
  if (e.name === 'CanceledError' || e.code === 'ERR_CANCELED') return false;
  const status = e.response?.status;
  if (status === undefined) {
    // 응답 자체가 없는 경우 — 네트워크 끊김·시간 초과
    return (
      e.code === 'ERR_NETWORK' ||
      e.code === 'ECONNABORTED' ||
      e.code === 'ETIMEDOUT'
    );
  }
  return status >= 500 || status === 408 || status === 429;
};

const isOffline = () =>
  typeof navigator !== 'undefined' && navigator.onLine === false;

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * 업로드를 일시적 실패에 한해 다시 시도한다. 마지막 실패는 그대로 던진다.
 * 오프라인이면 기다려도 소용없으므로 바로 던진다 — 백업 큐로 보내고 온라인 복구 때 다시 올린다.
 */
export const uploadWithRetry = async <T>(
  upload: (attempt: number) => Promise<T>,
  {
    delays = UPLOAD_RETRY_DELAYS_MS,
    onRetry,
    sleep = wait,
  }: {
    delays?: number[];
    onRetry?: (attempt: number, error: unknown) => void;
    sleep?: (ms: number) => Promise<unknown>;
  } = {},
): Promise<T> => {
  for (let attempt = 0; ; attempt++) {
    try {
      return await upload(attempt);
    } catch (e) {
      const canRetry =
        attempt < delays.length && isRetryableUploadError(e) && !isOffline();
      if (!canRetry) throw e;
      onRetry?.(attempt + 1, e);
      await sleep(delays[attempt]);
    }
  }
};
